import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-daisyui';
import Routes from '@/router/Routes.ts';
import * as z from 'zod';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import OtpInput from '@/components/functional/OtpInput/OtpInput.tsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchParams from '@/_constants/SearchParams.ts';

const confirmEmailSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Invalid email. Try again using link from email that you've received." })
    .email('Invalid email. Contact support.'),
  code: z.string().min(1, { message: 'This field is required.' }),
});

type ConfirmEmailSchema = z.infer<typeof confirmEmailSchema>;

const ConfirmEmailPage: FC = () => {
  const [searchParams] = useSearchParams();
  const email = decodeURIComponent(searchParams.get(SearchParams.EMAIL) ?? '');
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ConfirmEmailSchema>({
    resolver: zodResolver(confirmEmailSchema),
    defaultValues: {
      email: email,
      code: '',
    },
  });
  const { t } = useTranslation();
  const {
    verifyEmailQuery: {
      mutateAsync: verifyEmail,
      isPending: isLoadingVerifyEmail,
      error: errorVerifyEmail,
      isError: isErrorVerifyEmail,
      isSuccess: isSuccessVerifyEmail,
    },
    resendVerificationCodeQuery: {
      mutateAsync: resendVerificationCode,
      isPending: isLoadingVerificationCode,
      error: errorVerificationCode,
      isError: isErrorVerificationCode,
      isSuccess: isSuccessVerificationCode,
    },
  } = useAuthContext();

  const navigate = useNavigate();

  useEffect(() => {
    if (!shouldRedirect) return;
    const timer = setTimeout(() => {
      console.log('HERE!!!');
      navigate(Routes.ONBOARDING);
    }, 3000);
    return () => clearTimeout(timer);
  }, [shouldRedirect, navigate]);

  const handleVerifyEmail = (data: ConfirmEmailSchema) => {
    void (async () => {
      try {
        if (isLoadingVerifyEmail || !data.email) return;

        await verifyEmail({
          email: data.email,
          code: data.code,
        });

        setShouldRedirect(true);
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const handleResendCode = () => {
    void (async () => {
      try {
        if (isLoadingVerificationCode || !email) return;

        await resendVerificationCode({
          email: email,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const OTP_INPUT_CLASS =
    '!bg-[#FAFAFA] !border !border-[rgba(0,0,0,0.23)] !rounded text-[#1F2937] text-center font-dm';

  return (
    <BrandCardSideContent centerContent>
      <form className="flex flex-col gap-6 w-full" onSubmit={handleSubmit(handleVerifyEmail)}>
        <div className="flex flex-col gap-2">
          {errors['email'] ? (
            <Alert status="error">{errors['email']?.message}</Alert>
          ) : (
            <>
              <h1 className="font-newsreader font-bold text-2xl leading-[117%] text-[rgba(0,0,0,0.87)]">
                {t('registrationPage.successTitle')}
              </h1>
              <p className="font-dm text-sm font-normal leading-[150%] tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
                {t('registrationPage.successDescription')}
              </p>
            </>
          )}
        </div>
        {isSuccessVerifyEmail && <Alert status="success">{t('verifyEmailPage.successInfo')}</Alert>}
        {isSuccessVerificationCode && <Alert status="success">{t('verifyEmailPage.successInfoResendCode')}</Alert>}
        {isErrorVerifyEmail && <Alert status="error">{errorVerifyEmail?.message}</Alert>}
        {isErrorVerificationCode && <Alert status="error">{errorVerificationCode?.message}</Alert>}
        <div className="flex flex-col gap-3">
          <Controller
            control={control}
            name="code"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <OtpInput
                isInputNum
                placeholder="######"
                error={errors.code?.message}
                onChange={onChange}
                value={value}
                numInputs={6}
                inputClassName={OTP_INPUT_CLASS}
              />
            )}
          />
          <button
            type="button"
            onClick={handleResendCode}
            className="font-dm text-sm text-[#2196F3] underline text-left w-fit"
          >
            {t('button.resendCode')}
          </button>
        </div>
        <LoadingButton className="w-full" loading={isLoadingVerifyEmail} type="submit" color="primary">
          {t('button.continue')}
        </LoadingButton>
      </form>
    </BrandCardSideContent>
  );
};

export default ConfirmEmailPage;
